/**
 * JewelleryTagDialog — in-app (authed) wrapper around <JewelleryTagViewer>.
 * Opened from a card on the Jewellery page; fetches the full jewellery
 * record so the viewer can render the printable tag with its consumed raw
 * material, QR, layouts, and collapse/expand toggle.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Loader2, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { extractError } from '@/lib/errors';
import { JewelleryTagViewer, buildTagDataFromDetail } from '@/components/jewellery/JewelleryTagViewer';

export function JewelleryTagDialog({ open, onOpenChange, jewelleryId, jewelleryNo }) {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const data = useMemo(() => (detail ? buildTagDataFromDetail(detail) : null), [detail]);

  useEffect(() => {
    let cancelled = false;
    if (open && jewelleryId) {
      setLoading(true);
      setDetail(null);
      api.get(`/jewellery/${jewelleryId}`)
        .then((res) => { if (!cancelled) setDetail(res.data); })
        .catch((e) => { if (!cancelled) toast.error(extractError(e, 'Failed to load tag details')); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }
    return () => { cancelled = true; };
  }, [open, jewelleryId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="jewellery-tag-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer size={18} className="text-[var(--jewellery-icon)]" />
            Jewellery Tag — {jewelleryNo || data?.jewelleryNo || ''}
          </DialogTitle>
          <DialogDescription>
            Choose a layout, expand or collapse the raw-material list, then print. Scanning the QR opens this tag on any phone. No value or price is ever shown.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm py-10 justify-center" data-testid="jewellery-tag-loading">
            <Loader2 size={16} className="animate-spin" /> Loading tag…
          </div>
        ) : data ? (
          <JewelleryTagViewer data={data} defaultShowQr={false} piecesToggle />
        ) : (
          <div className="text-slate-400 text-sm py-10 text-center">No tag to display.</div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default JewelleryTagDialog;
